import { Layout, Table, Typography } from "antd";
import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from "react";
import axios from "api/axios";
import { useDpatIndicatorDataSource } from "hooks/useDpatIndicatorDataSource";
import DpatSubIndicatorSection from "../setup/DpatSubIndicatorSection";
import DpatIndicatorCmsIntro from "../setup/DpatIndicatorCmsIntro";
import MaxScoreDisplay from "../setup/MaxScoreDisplay";
import ActualScoreDisplay from "../setup/ActualScoreDisplay";
import IndicatorScoreSection from "../setup/IndicatorScoreSection";
import Comment from "components/Comments";
import {
  formatDataGeneral,
  getAttributeValue,
  getFileLinkIfExist,
  getFirstFileLinkIfExist,
  formatTrackerDate,
  getYearFromDateValue,
} from "utils/utils";
import { transformDpatTableRows, rowsHaveAllEvidenceLinks, isEvidenceLinkUploaded } from "utils/renderDpatEvidenceLink";
import { isDpatRegistrationYear, isDpatScoreRelaxationExempt } from "utils/dpatScoreRelaxation";
import { buildSearchableColumns, getDpatTablePagination } from "utils/dpatSearchableTable";

const FOOD_VENDOR_PROGRAM = "abiQOocP8YA";
const DOCUMENTS_HUB_PROGRAM = "SERtHvYVHRT";
const FOOD_MONITORING_DOC_TYPE = "Food and Beverage Vendors Monitoring Report";
const MONITORING_REPORT_FILE = "MjPmcbueEdU";
const FOOD_HYGIENE_REPORT_FILE = "zzoRCJPynMR";
const FOOD_VENDOR_REPORTS_STAGE = "PF70lMOfwK4";
const CERTIFICATE_FILE = "POdgz2rYh8H";

function resolveFoodVendorMonitoringReport(documents, docReports) {
  const monitoringDocs = formatDataGeneral(documents, "DPAT | M&E Tool Type", FOOD_MONITORING_DOC_TYPE) || [];

  for (const document of monitoringDocs) {
    const monitoringEventId = getFileLinkIfExist(docReports, MONITORING_REPORT_FILE, document.trackedEntity);
    if (monitoringEventId) {
      return {
        availability: "YES",
        report: `https://dddp.gov.gh/api/events/files?eventUid=${monitoringEventId}&dataElementUid=${MONITORING_REPORT_FILE}`,
      };
    }

    const hygieneEventId = getFirstFileLinkIfExist(
      docReports,
      FOOD_HYGIENE_REPORT_FILE,
      document.trackedEntity,
      FOOD_VENDOR_REPORTS_STAGE,
    );
    if (hygieneEventId) {
      return {
        availability: "YES",
        report: `https://dddp.gov.gh/api/events/files?eventUid=${hygieneEventId}&dataElementUid=${FOOD_HYGIENE_REPORT_FILE}`,
      };
    }
  }

  return { availability: "NO", report: "NO" };
}

const FoodVendors = forwardRef(({
  year,
  districtId,
  hideComment,
  savedAssessmentData,
  assessmentStartLoading,
  isAssessmentStarted,
}, ref) => {
  const [scorei, setScorei] = useState(0);
  const [scoreii, setScoreii] = useState(0);
  const [scoreiii, setScoreiii] = useState(0);
  const [percentage, setPercentage] = useState(0);
  const [data, setData] = useState([]);
  const [foodVendors, setFoodVendors] = useState([]);
  const [maxScore, setMaxScore] = useState(3);

  const { Content } = Layout;
  const { Title } = Typography;

  useImperativeHandle(ref, () => ({
    getData: () => ({
      indicator: "SDI5",
      area: "Environmental Health, Sanitation and Climate Action",
      maxScore,
      data,
      foodVendors,
      scorei,
      scoreii,
      scoreiii,
      percentage
    }),
  }));

  const DPAT_SLUG = "sdi5.0-5.3";


  const calculatePercentage = (total, value) => {
    const totalNum = parseFloat(total);
    const valueNum = parseFloat(value);

    if (isNaN(totalNum) || isNaN(valueNum) || totalNum === 0) {
      return 0;
    }

    return ((valueNum / totalNum) * 100).toFixed(2);
  };

  const loadFromDhis2 = useCallback(() => {
    const vendorRequest = axios.get(
      `/tracker/trackedEntities?orgUnit=${districtId}&program=${FOOD_VENDOR_PROGRAM}&pageSize=5000&fields=trackedEntity,createdAt,attributes[attribute,displayName,value]`,
    );
    const documentRequest = axios.get(
      `/tracker/trackedEntities?orgUnit=${districtId}&program=${DOCUMENTS_HUB_PROGRAM}&startDate=${year}-01-01&endDate=${year}-12-31&pageSize=5000`,
    );

    Promise.all([vendorRequest, documentRequest])
      .then(([vendorResult, documentResult]) => {
        const vendors = vendorResult.data?.instances || [];
        const documents = documentResult.data?.instances || [];

        const vendorEventsPromise = vendors.length > 0
          ? axios.get(`/tracker/events?program=${FOOD_VENDOR_PROGRAM}&orgUnit=${districtId}&startDate=${year}-01-01&endDate=${year}-12-31&pageSize=5000`)
          : Promise.resolve({ data: { instances: [] } });

        const documentEventsPromise = documents.length > 0
          ? axios.get(`/tracker/events?program=${DOCUMENTS_HUB_PROGRAM}&orgUnit=${districtId}&startDate=${year}-01-01&endDate=${year}-12-31&pageSize=5000`)
          : Promise.resolve({ data: { instances: [] } });

        return Promise.all([vendorEventsPromise, documentEventsPromise]).then(([vendorEvents, documentEvents]) => {
          const reports = vendorEvents.data?.instances || [];
          const docReports = documentEvents.data?.instances || [];

          const yearNum = Number(year);
          const vendorsUpToYear = vendors.filter((vendor) => {
            const vendorYear = getYearFromDateValue(vendor?.createdAt);
            if (isDpatScoreRelaxationExempt()) {
              return isDpatRegistrationYear(vendorYear, yearNum);
            }
            return vendorYear != null && Number.isFinite(yearNum) && vendorYear <= yearNum;
          });

          const vendorTotal = vendorsUpToYear.length;
          let screenedTotal = 0;
          let certificateTotal = 0;
          const foodTemp = [];
          const monitoringReport = resolveFoodVendorMonitoringReport(documents, docReports);

          vendorsUpToYear.forEach((vendor) => {
            const isScreened = getAttributeValue("DPAT | Is Food and beverage vendors screened?", vendor);
            const isCertified = getAttributeValue("DPAT | Is there a Health Certificate?", vendor);
            const certificateLink = getFileLinkIfExist(reports, CERTIFICATE_FILE, vendor.trackedEntity);

            foodTemp.push({
              key: vendor.trackedEntity,
              registrationDate: formatTrackerDate(vendor?.createdAt),
              name: getAttributeValue("DCACT | Food Vendors - Name of vendor / Business", vendor),
              phone: getAttributeValue("Phone", vendor),
              tin: getAttributeValue("TIN", vendor),
              card: getAttributeValue("Ghana Card Number", vendor),
              location: getAttributeValue("Location", vendor),
              certificate: certificateLink
                ? `https://dddp.gov.gh/api/events/files?eventUid=${certificateLink}&dataElementUid=${CERTIFICATE_FILE}`
                : "Not Uploaded",
            });

            if (isScreened === "true") {
              screenedTotal++;
            }

            if (isCertified === "true") {
              certificateTotal++;
            }
          });

          const percentage = calculatePercentage(screenedTotal, certificateTotal);
          const summaryRow = {
            key: "food-vendor-summary",
            availabilty: vendorTotal > 0 ? "YES" : "NO",
            vendorTotal,
            screenedTotal,
            certificateTotal,
            reportAvailability: monitoringReport.availability === "YES" ? monitoringReport.report : "NO",
          };

          setFoodVendors(foodTemp);
          setData([summaryRow]);
          setPercentage(percentage);

          const certificatesComplete = rowsHaveAllEvidenceLinks(foodTemp, ["certificate"]);
          const monitoringLinkUploaded = monitoringReport.availability === "YES"
            && isEvidenceLinkUploaded(monitoringReport.report);

          setScorei(vendorTotal > 0 && certificatesComplete ? 1 : 0);
          setScoreii(percentage >= 80 && certificatesComplete ? 1 : 0);
          setScoreiii(monitoringLinkUploaded ? 1 : 0);
        });
      })
      .catch((err) => console.log(err));
  }, [districtId, year]);

  useDpatIndicatorDataSource({
    isAssessmentStarted,
    savedAssessmentData,
    assessmentStartLoading,
    loadFromDhis2,
    applySnapshot: (snap) => {
      if (snap.data) setData(snap.data);
      if (snap.foodVendors) setFoodVendors(snap.foodVendors);
      if (snap.scorei != null) setScorei(snap.scorei);
      if (snap.scoreii != null) setScoreii(snap.scoreii);
      if (snap.scoreiii != null) setScoreiii(snap.scoreiii);
      if (snap.percentage != null) setPercentage(snap.percentage);
      if (snap.maxScore != null) setMaxScore(snap.maxScore);
    },
    deps: [year, districtId, loadFromDhis2],
  });

  const foodVendorsColumn = [
    { title: "Register of Food and Beverage Vendors available", dataIndex: "availabilty", key: "availabilty" },
    { title: "Number of Food and Beverage Vendors registered", dataIndex: "vendorTotal", key: "vendorTotal" },
    {
      title: `Number of Food and Beverage Vendors screened for the ${year}`, dataIndex: "screenedTotal", key: "screenedTotal"
    },
    {
      title: `Number of screened vendors issued with certificates for ${year}`, dataIndex: "certificateTotal", key: "certificateTotal"
    },
    { title: "Availability of Monitoring Reports (Yes/No)", dataIndex: "reportAvailability", key: "reportAvailability" }
  ];

  const vendorRows = useMemo(
    () => transformDpatTableRows(foodVendors, ["certificate"], { certificate: "View Certificate" }),
    [foodVendors],
  );

  const vendorsColumn = useMemo(
    () => buildSearchableColumns(
      [
        { title: "Registration Date", dataIndex: "registrationDate", key: "registrationDate" },
        { title: "Vendors", dataIndex: "name", key: "name" },
        { title: "Phone", dataIndex: "phone", key: "phone" },
        { title: "TIN", dataIndex: "tin", key: "tin" },
        { title: "Ghana Card", dataIndex: "card", key: "card" },
        { title: "Location", dataIndex: "location", key: "location" },
        { title: "Certificate", dataIndex: "certificate", key: "certificate" },
      ],
      foodVendors,
      {
        textSearchKeys: ["name", "phone", "tin", "card", "location", "registrationDate"],
      },
    ),
    [foodVendors],
  );

  return (
    <Comment
      data={data}
      year={year}
      districtId={districtId}
      tableCommentedId={`sdi5.0-5.3-${year}`}
      hideComment={hideComment}
    >
      {({ renderCommentFeedbackFooter }) => (
          <DpatSubIndicatorSection code={DPAT_SLUG}>
          <DpatIndicatorCmsIntro
            year={year}
            slug={DPAT_SLUG}
            dhis2ProgramId={FOOD_VENDOR_PROGRAM}
            movDefaultText="Register for all identified food vendors both day and night markets in the district/municipality/metropolis."
          />

          <IndicatorScoreSection>
          <MaxScoreDisplay score={maxScore} />
                    <ActualScoreDisplay slug={DPAT_SLUG} label="SDI 5.0-5.3i Actual Score:" score={scorei} />
          <ActualScoreDisplay slug={DPAT_SLUG} label="SDI 5.0-5.3ii Actual Score:" score={scoreii} />
          <ActualScoreDisplay slug={DPAT_SLUG} label="SDI 5.0-5.3iii Actual Score:" score={scoreiii} />
          </IndicatorScoreSection>
          <Title level={4} style={{ marginTop: "20px" }}>Evidence of Registration & Screening of Food Vendors</Title>
          <Table
            columns={foodVendorsColumn}
            dataSource={transformDpatTableRows(data || [], ["reportAvailability"], { reportAvailability: "View Report" })}
            pagination={false}
            bordered
            rowKey="key"
          />

          <Title level={4} style={{ marginTop: "20px" }}>Evidence of Registration of Food Vendors</Title>
          <Table
            columns={vendorsColumn}
            dataSource={vendorRows}
            pagination={getDpatTablePagination("vendors")}
            bordered
            rowKey="key"
          />

          <Title level={5} style={{ marginTop: "20px" }}>Conclusion</Title>
          <p>
            {percentage}% of the screened food vendors were issued with certificates in {year}
          </p>
            {!hideComment && renderCommentFeedbackFooter()}
          </DpatSubIndicatorSection>
      )}
    </Comment>
  );
})

export default FoodVendors;